// 🔧 promotion_settings_map.js
// ✅ Purpose: Central schema definition for the LGV Promotion Engine
// ✅ Features:
// - Defines all Firestore collection names
// - Defines document IDs
// - Defines common field names
// - Defines storage folder names
// - Provides a single source of truth for Promotion Engine schema
// - Fully block-commented for clarity and future devs

/**
 * ============================================================================
 * FIRESTORE COLLECTIONS
 * ============================================================================
 *
 * All Promotion Engine collections should reference these constants instead
 * of hardcoding collection names throughout the application.
 */

export const PROMOTION_COLLECTIONS = {
  SETTINGS: "promotion_settings",

  IDEAS: "promotion_ideas",

  /**
   * promotion_posts is the master content collection.
   *
   * A post moves through its lifecycle using
   * queueStatus and publishStatus.
   *
   * Examples:
   *
   * queueStatus = draft
   * publishStatus = not_ready
   *
   * queueStatus = approved
   * publishStatus = scheduled
   *
   * queueStatus = approved
   * publishStatus = published
   *
   * This avoids having separate collections for
   * drafts, scheduled items, published posts,
   * failed posts, etc.
   */
  POSTS: "promotion_posts",

  HISTORY: "promotion_history",

  FOLLOW_TARGETS: "promotion_follow_targets",

  CAMPAIGNS: "promotion_campaigns",

  METRICS: "promotion_metrics",
};

/**
 * ============================================================================
 * STANDARD DOCUMENT IDS
 * ============================================================================
 */

export const PROMOTION_DOCUMENTS = {
  SETTINGS: "main",
};

/**
 * ============================================================================
 * STORAGE PATHS
 * ============================================================================
 */

export const PROMOTION_STORAGE = {
  GENERATED_IMAGES: "promotion/generated-images",
  MANUAL_IMAGES: "promotion/manual-images",
};

/**
 * ============================================================================
 * COMMON FIELD NAMES
 * ============================================================================
 *
 * Shared Firestore field names used throughout the Promotion Engine.
 * Using constants helps eliminate typos and keeps the schema consistent.
 */

export const PROMOTION_FIELDS = {
  ID: "id",

  TITLE: "title",
  DESCRIPTION: "description",

  CREATED_AT: "createdAt",
  UPDATED_AT: "updatedAt",
  CREATED_BY: "createdBy",
  UPDATED_BY: "updatedBy",

  SCOPE: "scope",
  EVENT_ID: "eventId",
  ORGANIZATION_ID: "organizationId",

  CATEGORY: "category",
  CAMPAIGN: "campaign",
  THEME: "theme",

  PLATFORM: "platform",
  PLATFORM_FEATURE: "platformFeature",
  POST_TYPE: "postType",

  AUDIENCE: "audience",
  GOAL: "goal",

  IMAGE_URL: "imageUrl",
  IMAGE_STORAGE_PATH: "imageStoragePath",
  IMAGE_PROMPT: "imagePrompt",

  SCHEDULED_AT: "scheduledAt",
  POSTED_AT: "postedAt",

  IDEA_STATUS: "ideaStatus",
  QUEUE_STATUS: "queueStatus",
  IMAGE_STATUS: "imageStatus",
  PUBLISH_STATUS: "publishStatus",

  AI_GENERATED: "aiGenerated",
  ENGINE_SOURCE: "engineSource",

  PLATFORM_POST_ID: "platformPostId",

  ERROR_MESSAGE: "errorMessage",
};

/**
 * ============================================================================
 * ENGINE SCOPES
 * ============================================================================
 *
 * Internal LGV marketing vs future customer event marketing.
 */

export const PROMOTION_SCOPES = {
  INTERNAL_LGV: "internal_lgv",
  EVENT: "event",
};

/**
 * ============================================================================
 * ENGINE SOURCES
 * ============================================================================
 *
 * Identifies where promotional content originated.
 * MVP 1 will primarily use MANUAL, AI, and PROSPECTS.
 * Future engines will contribute automatically.
 */

export const PROMOTION_ENGINE_SOURCES = {
  MANUAL: "manual",
  AI: "ai",

  PROSPECTS: "prospects",

  FEATURES: "features",

  ENGINES: "engines",

  VOTING: "voting",

  SPONSORS: "sponsors",

  VENDORS: "vendors",

  VOLUNTEERS: "volunteers",

  TICKETS: "tickets",

  AUCTIONS: "auctions",

  PASSPORT: "passport",

  SCHEDULE: "schedule",

  WINNERS: "winners",

  DASHBOARD: "dashboard",
};

/**
 * ============================================================================
 * DEFAULT SETTINGS
 * ============================================================================
 *
 * Default configuration values used when the Promotion Engine
 * is initialized for the first time.
 */

export const PROMOTION_DEFAULT_SETTINGS = {
  enabled: true,

  aiEnabled: true,

  imageGenerationEnabled: true,

  autoSchedulingEnabled: false,

  defaultScope: PROMOTION_SCOPES.INTERNAL_LGV,

  maxAiGenerationsPerRun: 10,

  maxScheduledPostsPerRun: 20,
};
